/**
 * Parâmetros de agenda que o `core` consome.
 *
 * São exatamente os campos que o administrador edita em tela (TenantSettings no
 * banco). Nenhum deles tem — nem pode ter — equivalente em variável de ambiente:
 * duas barbearias no mesmo servidor têm regras diferentes.
 */
export interface ScheduleSettings {
  /** Granularidade dos horários oferecidos, em minutos. */
  slotStepMinutes: number;

  /**
   * Grade fixa: os horários saem do começo de cada faixa do expediente, de
   * passo em passo — 09:00, 09:40, 10:20 —, e não do fim do atendimento
   * anterior. Desligada, a escada se reancora a cada atendimento (ver
   * `findAvailableSlots`). É escolha da barbearia: há quem prefira o dia
   * rendendo e há quem prefira o cliente sabendo de cor os horários da casa.
   */
  fixedSlotGrid: boolean;

  /**
   * Quantos minutos um atendimento pode invadir o anterior sem o sistema
   * recusar. Vale para o cliente; o barbeiro encaixa por cima disso, com aviso.
   */
  overlapToleranceMinutes: number;

  /** Antecedência mínima entre agora e o início do atendimento. */
  minLeadTimeMinutes: number;

  /** Até quando o cliente ainda cancela sozinho. */
  minCancelNoticeMinutes: number;

  /** Quão longe no futuro dá para agendar. */
  maxFutureDays: number;
}

export const DEFAULT_SCHEDULE_SETTINGS: ScheduleSettings = {
  slotStepMinutes: 15,
  fixedSlotGrid: false,
  overlapToleranceMinutes: 0,
  minLeadTimeMinutes: 60,
  minCancelNoticeMinutes: 120,
  maxFutureDays: 60,
};

/** 40, 45 e 60 só fazem sentido com a grade fixa, mas valem para as duas. */
export const SLOT_STEP_OPTIONS = [5, 10, 15, 20, 30, 40, 45, 60] as const;
export type SlotStep = (typeof SLOT_STEP_OPTIONS)[number];

export function isValidSlotStep(value: number): value is SlotStep {
  return (SLOT_STEP_OPTIONS as readonly number[]).includes(value);
}
